// SignInPageGuest.tsx
// NOTE - The Bot Handling logic for this SignInPage is located in ../components/header/Header.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import useDeviceType from "../utils/getAgent";
import { OrgSettings } from '../tools/orgSettings';
import { fetchAgentBootstrap, orgFromBootstrap, bootstrapBotId } from '../utils/agentBootstrap';
import { PreAuthNotice } from '../components/header/PreAuthNotice';

import { signInGuest } from '../utils/guestSignIn';
import { claimGuestSignIn, releaseGuestSignIn, purgeStaleAuthKeys } from '../utils/guestGate';

const GUEST_SIGNIN_INFLIGHT_KEY = 'guestSignInInFlight';
const GUEST_SIGNIN_COOLDOWN_UNTIL_KEY = 'guestSignInCooldownUntil';

const SignInPageGuest = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const [isGuestSigningIn, setIsGuestSigningIn] = useState(false);
  const [guestSignInError, setGuestSignInError] = useState<string | null>(null);
  const deviceType = useDeviceType(); 
  const queryParams = new URLSearchParams(window.location.search);
  const resolveInitialBotId = () => {
    const fromQuery = queryParams.get('id');
    if (fromQuery) return fromQuery;

    const fromLastBot = localStorage.getItem('lastBotId');
    if (fromLastBot) return fromLastBot;

    const fromSelectedBot = localStorage.getItem('selectedBot');
    if (fromSelectedBot) return fromSelectedBot;

    try {
      const rawSelected = localStorage.getItem('SelectedBot');
      if (rawSelected) {
        const parsed = JSON.parse(rawSelected);
        if (parsed?.botid) return parsed.botid as string;
      }
    } catch {
      // ignore parse issues
    }

    try {
      const guestOrigin = sessionStorage.getItem('guestOrigin') || localStorage.getItem('guestOrigin');
      if (guestOrigin) {
        const originUrl = new URL(guestOrigin);
        const originId = originUrl.searchParams.get('id');
        if (originId) return originId;
      }
    } catch {
      // ignore parse issues
    }

    return null;
  };
  const [botId, setBotId] = useState<string | null>(resolveInitialBotId());
  const [prompt, setPrompt] = useState<string | null>(queryParams.get("prompt"));
  const [org, setOrg] = useState<OrgSettings | null>(null);
  // [S2-B B2] decision A: the bot must resolve from the DB before a guest session is minted for it.
  const [preAuthFailure, setPreAuthFailure] = useState<null | 'not-found' | 'unavailable' | 'choose'>(null);
  const [backgroundColor, setBackgroundColor] = useState<string>('#fff');
  const resolvedBackground = org?.containerStyle?.backgroundColor || backgroundColor || '#fff';
  const resolvedBodyColor = org?.bodyStyle?.color || '#333333';
  const resolvedFont = org?.fontFace || 'Roboto, Arial, sans-serif';
  const didAttemptRef = useRef(false);

  const logBackNav = (msg: string, extra?: Record<string, any>) => {
    try {
      console.log(`[GuestSignIn][BackNav] ${msg}`, extra ? JSON.stringify(extra) : '');
    } catch {
      // ignore logging issues
    }
  };

  const storeOrigin = (href: string) => {
    try {
      sessionStorage.setItem('guestOrigin', href);
      localStorage.setItem('guestOrigin', href);
      logBackNav('Stored guestOrigin', { href });
    } catch (e) {
      logBackNav('Failed to store guestOrigin', { error: (e as any)?.message });
    }
  };

  const getStoredOrigin = () => {
    try {
      const ses = sessionStorage.getItem('guestOrigin');
      const loc = localStorage.getItem('guestOrigin');
      return ses || loc || null;
    } catch {
      return null;
    }
  };

  const isSameOrigin = (href: string) => {
    try {
      const u = new URL(href);
      return u.origin === window.location.origin;
    } catch {
      return false;
    }
  };

  const applyOrgBackground = useCallback((orgConfig?: OrgSettings | null) => {
    const bg = orgConfig?.containerStyle?.backgroundColor || '#fff';
    document.body.style.backgroundColor = bg;
    document.documentElement.style.backgroundColor = bg;
    const root = document.getElementById('root');
    if (root) {
      root.style.backgroundColor = bg;
    }
  }, []);

  // Initialize botId and org
  useEffect(() => {
  if (didAttemptRef.current) {
    return;
  }
  didAttemptRef.current = true;

  let cancelled = false;
  let currentBotId = botId;

  // Capture where the user came from so we can restore on back/forward nav
  if (document.referrer && document.referrer !== window.location.href) {
    storeOrigin(document.referrer);
  }

  // If the user navigated back to this page, immediately return them to where they came from
  try {
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const navType = navEntries?.[0]?.type || (performance as any).navigation?.type;
    const originHref = getStoredOrigin();

    logBackNav('Navigation detected', { navType, originHref, referrer: document.referrer });

    if (navType === 'back_forward') {
      if (originHref && isSameOrigin(originHref)) {
        logBackNav('Back/forward nav detected, redirecting to stored origin', { originHref });
        window.location.replace(originHref);
        return;
      }
      logBackNav('Back/forward nav detected, no stored origin — using history.go(-1)');
      window.history.go(-1);
      return;
    }
  } catch {
    // non-blocking; continue normal flow
  }

  // [S2-B B2] No Nova default, no compiled `orgs` map: the org is resolved from the DB
  // (GET /agent-bootstrap) at the top of the sign-in IIFE below, BEFORE any mint.

  // Clean up local storage
  // [S2-A] Purge the stale-auth key set BEFORE minting — a leftover tokenTimestamp / token /
  // guestMode / orgId from a previous session is what re-armed the redirect loop (F5). The 429
  // cooldown and the in-flight latch are KEPT: this page is about to read the one and claim the
  // other, and purging them here would defeat the rate-limit backoff and the double-mount guard.
  purgeStaleAuthKeys(
    { localStorage, sessionStorage, document },
    { keep: [GUEST_SIGNIN_COOLDOWN_UNTIL_KEY, GUEST_SIGNIN_INFLIGHT_KEY] },
  );
  localStorage.removeItem("user");
  localStorage.removeItem("org");
  localStorage.removeItem("lastBotId");
  localStorage.removeItem("botId");

  setBotId(currentBotId);
  // Guest Sign In with Prompt
  (async () => {
    try {
      // [S2-B B2] Resolve the bot (by ?id=/stored id, else by host) from the DB first. Hard fail:
      // not-found / unavailable ⇒ render the state and do NOT mint a guest for a bot that has no record.
      const boot: any = await fetchAgentBootstrap(
        currentBotId ? { id: currentBotId } : { host: window.location.hostname },
        { baseUrl: (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '') || undefined },
      );
      if (cancelled) return;
      if (boot.status !== 'ok') {
        console.error(`[BotIdentity] guest pre-auth ${boot.status}: ${currentBotId || window.location.hostname} — ${boot.reason}`);
        setPreAuthFailure(!currentBotId && boot.status === 'not-found' ? 'choose' : boot.status);
        return;
      }
      if (!currentBotId) {
        currentBotId = bootstrapBotId(boot.record);
        setBotId(currentBotId);
      }
      const resolvedOrg = orgFromBootstrap(boot.record, currentBotId || undefined) as OrgSettings;
      setOrg(resolvedOrg);
      setBackgroundColor(resolvedOrg?.containerStyle?.backgroundColor || '#fff');
      applyOrgBackground(resolvedOrg);
      localStorage.setItem("org", JSON.stringify(resolvedOrg));
      const cooldownUntilRaw = localStorage.getItem(GUEST_SIGNIN_COOLDOWN_UNTIL_KEY) || '0';
      const cooldownUntil = Number(cooldownUntilRaw);
      const now = Date.now();
      if (!Number.isNaN(cooldownUntil) && cooldownUntil > now) {
        const minutesLeft = Math.max(1, Math.ceil((cooldownUntil - now) / 60000));
        setGuestSignInError(`Guest sign-in is temporarily rate-limited. Please try again in about ${minutesLeft} minute(s).`);
        return;
      }

      // ⛔ RECOVERABLE LATCH (2026-08-14). This used to read a bare `'1'` and return without
      // minting AND without redirecting — a dead-end splash that survived for the life of the tab,
      // because React does not run effect cleanup when the browser navigates away mid-mint.
      // A timestamped claim can tell "in flight now" from "abandoned earlier"; only the first
      // should skip.
      const claim = claimGuestSignIn(sessionStorage, GUEST_SIGNIN_INFLIGHT_KEY);
      if (!claim.claimed) {
        logBackNav('Guest sign-in already in flight, skipping duplicate request', { ageMs: claim.ageMs });
        return;
      }
      if (claim.staleReclaim) {
        logBackNav('Reclaimed a STALE guest sign-in latch — a previous attempt was abandoned mid-flight',
          { ageMs: claim.ageMs });
      }
      setIsGuestSigningIn(true);
      // [S2-A] Mint FOR this bot: the org is resolved from its BotSettings record. `currentBotId`
      // is the URL's ?id= when present (resolveInitialBotId reads it first) and otherwise the
      // same stored fallback the redirect below targets — mint and redirect agree by construction.
      const response = await signInGuest(prompt || '', currentBotId);
      if (response) {
        logBackNav('Guest sign-in succeeded, redirecting', { botId: currentBotId, prompt });
        localStorage.removeItem(GUEST_SIGNIN_COOLDOWN_UNTIL_KEY);
        // Build redirect URL with all required parameters
        const redirectUrl = new URL(window.location.origin);
        redirectUrl.pathname = '/ibot-guest';
        redirectUrl.searchParams.set('id', currentBotId || 'asst_M3yCPGj7R4YKciQ4ZyNNyzxT');
        if (prompt) {
          redirectUrl.searchParams.set('prompt', prompt);
          console.log('-> Redirecting with prompt:', prompt);


        }
        window.location.href = redirectUrl.toString();
      } else {
        if (!cancelled) {
          setIsGuestSigningIn(false);
          setGuestSignInError("Failed to sign in as guest. Please try again.");
        }
      }
    } catch (error: any) {
      logBackNav('Guest sign-in error', { error: (error as any)?.message });
      const statusCode = error?.response?.status || error?.response?.data?.error?.statusCode;
      const apiMessage = error?.response?.data?.error?.message as string | undefined;

      if (statusCode === 429) {
        const minutesMatch = apiMessage?.match(/(\d+)\s*mins?/i);
        const waitMinutes = minutesMatch?.[1] ? Number(minutesMatch[1]) : 15;
        const cooldownUntil = Date.now() + waitMinutes * 60 * 1000;
        localStorage.setItem(GUEST_SIGNIN_COOLDOWN_UNTIL_KEY, String(cooldownUntil));
        if (!cancelled) {
          setGuestSignInError(`Too many guest sign-in attempts. Please wait about ${waitMinutes} minute(s) before trying again.`);
        }
      } else if (!cancelled) {
        setGuestSignInError("Failed to sign in as guest. Please try again.");
      }
      if (!cancelled) {
        setIsGuestSigningIn(false);
      }
    } finally {
      releaseGuestSignIn(sessionStorage, GUEST_SIGNIN_INFLIGHT_KEY);
    }
  })();

  // ⚠️ `pagehide` is the one that matters: React's unmount cleanup below does NOT fire on a real
  // browser navigation, which is precisely how the latch got stranded.
  const releaseOnLeave = () => releaseGuestSignIn(sessionStorage, GUEST_SIGNIN_INFLIGHT_KEY);
  window.addEventListener('pagehide', releaseOnLeave);

  return () => {
    cancelled = true;
    window.removeEventListener('pagehide', releaseOnLeave);
    releaseGuestSignIn(sessionStorage, GUEST_SIGNIN_INFLIGHT_KEY);
  };

  }, [applyOrgBackground, botId, prompt]);

  // Handle guest sign-in    

  // Loading state for guest sign-in
  if (isGuestSigningIn) {
    return (
      <div className="container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        textAlign: 'center'
      }}>
        <div>
          <div className="loading">Signing in as guest...</div>
          <p style={{ marginTop: '1rem' }}>Please wait while we connect you.</p>
        </div>
      </div>
    );
  }

  // Error state for guest sign-in
  if (guestSignInError) {
    return (
      <div className="container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <div>
          <div className="error-message" style={{ 
            color: '#ff4d4f',
            marginBottom: '2rem',
            fontSize: '1.1rem'
          }}>
            {guestSignInError}
          </div>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button 
              onClick={() => window.location.reload()}
              className="signInButton"
              style={{
                padding: '0.5rem 1.5rem',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
            <button 
              onClick={() => window.location.href = '/'}
              className="signUpButton"
              style={{
                padding: '0.5rem 1.5rem',
                fontSize: '1rem',
                cursor: 'pointer',
                backgroundColor: '#f0f0f0',
                border: '1px solid #d9d9d9'
              }}
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main sign-in form
  if (preAuthFailure) return <PreAuthNotice status={preAuthFailure} />;

  return (
    <div className="signInWrapper" style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: resolvedBackground,
      fontFamily: resolvedFont,
      margin: 0,
      padding: 0
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;700&display=swap');
        body, html, #root {
          background-color: ${resolvedBackground} !important;
          font-family: ${resolvedFont};
        }
        .signInPageContainer {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          width: 100%;
          margin: 0 auto;
          background-color: ${resolvedBackground};
          color: ${resolvedBodyColor};
        }
        .signInContainer {
          max-width: 400px;
          margin: 0 auto;
          padding: 40px 20px;
          width: 100%;
          flex: 1;
        }
        .signInUp-logo-container {
          text-align: center;
          margin-bottom: 30px;
        }
        .signInUp-header-logo {
          max-width: 200px;
          height: auto;
          margin: 0 auto;
        }
        .signInTitleBlock {
          text-align: center;
          margin-bottom: 30px;
        }
        .title {
          color: ${org?.bodyStyle?.color || '#9e9e9e7d'} !important;
          margin: 0 0 10px !important;
          font-size: 24px !important;
          font-weight: 700 !important;
        }
        .subtitle {
          color: ${org?.bodyStyle?.color || '#666666'} !important;
          margin: 10px 0 !important;
          font-size: 16px !important;
          font-weight: 400 !important;
        }
        .edition {
          color: ${org?.bodyStyle?.color || '#888888'} !important;
          font-style: italic !important;
          margin: 5px 0 0 !important;
          font-size: 14px !important;
          font-weight: 300 !important;
        }
        input {
          width: 100%;
          padding: 12px 15px;
          margin: 8px 0;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 16px;
        }
        .inputGroup {
          position: relative;
          margin-bottom: 15px;
        }
        .infoText {
          text-align: center;
          margin: 20px 0;
          color: #666;
          font-size: 14px;
        }
        .buttonContainer {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-top: 20px;
        }
        .signInButton, .signUpButton {
          background-color: ${org?.buttonStyle?.backgroundColor || '#9e9e9e7d'} !important;
          color: ${org?.buttonStyle?.color || '#ffffff'} !important;
          border: none;
          padding: 12px 20px;
          border-radius: 4px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s ease;
          width: 100%;
        }
        
        .signInButton:hover, .signUpButton:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }
        
        .signInButton:active, .signUpButton:active {
          transform: translateY(0);
        }
        .errorStatus {
          color: #d32f2f !important;
          background-color: #ffebee !important;
          padding: 10px 15px;
          border-radius: 4px;
          margin: 0 0 20px;
          font-size: 14px;
          text-align: center;
        }
        
        @media (max-width: 480px) {
          .signInContainer {
            padding: 20px 15px !important;
          }
          .title {
            font-size: 22px !important;
          }
          .subtitle {
            font-size: 15px !important;
          }
        }
      `}</style>

      <div
        className={`signInPageContainer ${deviceType}`}
        style={{ color: resolvedBodyColor, backgroundColor: resolvedBackground }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div
            id="signInContainer"
            className="signInContainer"
            style={{
              ...org?.containerStyle,
              flexGrow: 1,
              overflowY: "auto",
              paddingBottom: "6rem",
            }}
          >
            {errorMessage && <div className="errorStatus">{errorMessage}</div>}

            <div className="signInUp-logo-container" id="signInUp-logo-container">
              <a href={org?.homepage || "https://www.cloudmediaworks.com"} target="_blank" rel="noopener noreferrer">
                <img
                  id="signInUp-header-logo"
                  className="signInUp-header-logo header-toolbar-image"
                  src={org?.logo || "/assets/app/logo192.png"}
                  alt={org?.name ? `${org.name} Thumbnail` : "logo"}
                />
              </a>
            </div>

            <div className="signInTitleBlock">
              <h2 className="title">{org?.title || "ROBOT FACTORY"}</h2>
              <hr id="hr-guest" />
              <h3 className="subtitle">{org?.subtitle || "High Performance Leaders"}</h3>
              <p className="edition">{org?.edition || "Skills Edition"}</p>
            </div>


            <div id="spacer" style={{ height: "100px" }}></div>
          </div>
        </div>

        <div
          id="signInFooter"
          className="signInFooter"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            padding: "1em 1em",
            width: "100%",
            fontSize:".7em",
            marginTop: "auto", 
          }}
        >
          <div style={{ textAlign: "center" }}>
            {org ? (
              <div className="footerDiv" id="signInDisclaimer-links">
                {org.footer.disclaimer}
                <br />
                <span>{org.footer.copyright}</span>
                {org.footer.links.map((link, index) => (
                  <React.Fragment key={index}>
                    &nbsp;|&nbsp;
                    <a href={link.href} target="_blank" rel="noopener noreferrer">
                      <span>{link.label}</span>
                    </a>
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <div className="footerDiv" id="signInDisclaimer-text">
                <br/>
                <a href="https://www.cloudmediaworks.com/terms-of-use" target="_blank" rel="noopener noreferrer">
                  Terms of Use&nbsp;|&nbsp;
                </a>
                &nbsp;|&nbsp;
                <a href="https://www.cloudmediaworks.com/privacy" target="_blank" rel="noopener noreferrer">
                  <span>Privacy Statement</span>&nbsp;|&nbsp;
                </a>
                &nbsp;|&nbsp;
                <a href="https://www.cloudmediaworks.com/cookies" target="_blank" rel="noopener noreferrer">
                  <span>Cookies Settings</span>&nbsp;|&nbsp;
                </a>&nbsp;&nbsp;&nbsp;
                &nbsp;|&nbsp;
              </div>
            )}
            <div>
              <a href="/admin" style={{ width: '29px', height: '29px', position: 'absolute', left: '94vw', bottom: '10px'}}>
                <span></span>
              </a>
            </div>
            <br />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInPageGuest;
